/* eslint-disable react-hooks/exhaustive-deps */
import { ServerConfig } from "@xapp/stentor-chat-widget";
import { createContext, useEffect, useRef, useState } from "react";
import { throwBadKind } from "../utils/throwBadKind";
import { useChatDispatch } from "../utils/useChatDispatch";
import { log } from "../utils";
import { ChatServer, ChatServerOptions } from "./ChatServer";
import { LogChat } from "./LogChat";
import { MCPChat } from "./MCPChat";
import { StentorDirectChat } from "./StentorDirectChat";
import { StentorLocalChat } from "./StentorLocalChat";
import { StentorRouterChat } from "./StentorRouterChat";
import { StentorServerChat } from "./StentorServerChat";

function createChatServerCore(config: ServerConfig, options?: ChatServerOptions): ChatServer {
    switch (config.type) {
        case "direct":
            return new StentorDirectChat(
                {
                    url: config.serverUrl,
                    key: config.accountKey,
                    timeout: config.timeout,
                },
                options
            );
        case "websocket":
            return new StentorServerChat(
                {
                    url: config.serverUrl,
                },
                options
            );
        case "websocketraw":
            return new StentorRouterChat(
                {
                    url: config.serverUrl,
                },
                options
            );
        case "mcp":
            return new MCPChat(
                {
                    serverUrl: config.serverUrl,
                    mode: config.mcp?.mode,
                    authToken: config.mcp?.authToken,
                    debugMode: config.mcp?.debugMode,
                    pageContextOverride: config.mcp?.pageContextOverride,
                    showAiBadge: config.mcp?.showAiBadge,
                },
                options
            );
        case "local":
            return new StentorLocalChat();
        default:
            throw throwBadKind(config.type);
    }
}

export function createChatServer(config: ServerConfig, options?: ChatServerOptions): ChatServer {
    return new LogChat(createChatServerCore(config, options));
}

export const ChatServerContext = createContext<ChatServer>(null);

export function useChatServer(
    config: ServerConfig | null,
    options?: ChatServerOptions | null
): ChatServer {
    const [server, setServer] = useState<ChatServer>();

    const dispatch = useChatDispatch();

    const deps = [options, config, dispatch];

    // Track previous values to detect what changed
    const prevDepsRef = useRef<typeof deps | undefined>(undefined);

    // Log what caused the "effect"
    // useWhatChanged(deps, "options, config, dispatch");

    useEffect(() => {
        // Log when effect runs and what changed (for debugging connection issues)
        if (prevDepsRef.current) {
            const [prevOptions, prevConfig, prevDispatch] = prevDepsRef.current;
            const changes: string[] = [];

            if (prevOptions !== options) changes.push("options");
            if (prevConfig !== config) changes.push("config");
            if (prevDispatch !== dispatch) changes.push("dispatch");

            if (changes.length > 0) {
                log(`[useChatServer] Effect re-running due to: ${changes.join(", ")}`);
            }
        } else {
            log(`[useChatServer] Effect running for first time`);
        }

        prevDepsRef.current = [options, config, dispatch];

        // Don't create server if config is null (waiting for async config load)
        if (!config || !options) {
            log(`[useChatServer] Config or options null, not creating server`);
            setServer(undefined);
            return undefined;
        }

        log(`Creating new chat server - type: ${config.type}`);
        const newServer = createChatServer(config, options);

        newServer.init(dispatch);

        setServer(newServer);
        return () => {
            log(`Cleanup - disposing old server`);
            newServer.dispose();
        };
    }, deps);

    return server;
}
